/**
 * The school vocabulary a publish asks for: who the material is for.
 *
 * These live here rather than inside the capsule publish dialog because they are
 * not the dialog's — a published book carries the *codes* (`Level_B1`, `Maths`)
 * and anything that shows a book has to be able to name them: the feed card, the
 * flashbook publisher, and whatever asks the same question next. A dialog that
 * both defines the vocabulary and is the only way to reach it makes every reader
 * of it import a dialog.
 */
import {type MacroMessageDescriptor, msg} from '@lingui/core/macro'

/**
 * The three school stages. `label` is what the *picker* shows — an emoji to
 * scan a short list by — and `shortLabel` is what a *card* shows, where an
 * emoji is noise and the word has to stand on its own in a chip.
 */
export const CAPSULE_CATEGORIES = [
  {
    label: msg`👦🏻 Primary School`,
    shortLabel: msg`Primary`,
    value: 'primary',
  },
  {label: msg`🧑🏻‍💻 College`, shortLabel: msg`College`, value: 'college'},
  {
    label: msg`🧑🏻‍🎓 Secondary`,
    shortLabel: msg`Secondary`,
    value: 'secondary',
  },
]

export const CAPSULE_GRADES: Record<
  string,
  {
    label: ReturnType<typeof msg>
    shortLabel: ReturnType<typeof msg>
    value: string
  }[]
> = {
  primary: [
    {label: msg`1️⃣ 1st year`, shortLabel: msg`1st year`, value: 'Level_A1'},
    {label: msg`2️⃣ 2nd year`, shortLabel: msg`2nd year`, value: 'Level_A2'},
    {label: msg`3️⃣ 3rd year`, shortLabel: msg`3rd year`, value: 'Level_A3'},
    {label: msg`4️⃣ 4th year`, shortLabel: msg`4th year`, value: 'Level_A4'},
    {label: msg`5️⃣ 5th year`, shortLabel: msg`5th year`, value: 'Level_A5'},
    {label: msg`6️⃣ 6th year`, shortLabel: msg`6th year`, value: 'Level_A6'},
  ],
  college: [
    {label: msg`7️⃣ 7th year`, shortLabel: msg`7th year`, value: 'Level_B1'},
    {label: msg`8️⃣ 8th year`, shortLabel: msg`8th year`, value: 'Level_B2'},
    {label: msg`9️⃣ 9th year`, shortLabel: msg`9th year`, value: 'Level_B3'},
  ],
  secondary: [
    {label: msg`1️⃣ 1st year`, shortLabel: msg`1st year`, value: 'Level_S1'},
    {label: msg`2️⃣ 2nd year`, shortLabel: msg`2nd year`, value: 'Level_S2'},
    {label: msg`3️⃣ 3rd year`, shortLabel: msg`3rd year`, value: 'Level_S3'},
    {label: msg`4️⃣ BAC`, shortLabel: msg`BAC`, value: 'Level_S4'},
  ],
}

/**
 * Kindergarten's own years.
 *
 * The record vocabulary starts at primary school, so a kindergarten classroom's
 * year has no code to borrow — these are the classroom's own, the way a private
 * school's "System 3" is its own name for a level. They are used for the room's
 * own identity (display and matching) and not for content levels, which the
 * record vocabulary does not reach down to.
 */
export const KINDERGARTEN_GRADES = [
  {label: msg`KG 1`, value: 'KG1'},
  {label: msg`KG 2`, value: 'KG2'},
  {label: msg`KG 3`, value: 'KG3'},
]

/**
 * The years a classroom can be pinned to, per classroom level.
 *
 * Five classroom levels are school years — Kindergarten, Primary School, Middle
 * School, Secondary School and High School — and a classroom at one of them
 * names the year it teaches. The other levels (University, Other) have no year
 * of their own, and a classroom with no level at all is open to every learner.
 *
 * **Secondary School and High School are the same years under two names**: the
 * app's own learner stage is `High School` while the record vocabulary's shared
 * level is `Secondary School`, and a school that thinks of its years as "high
 * school" should be able to pin a room exactly as one that thinks of them as
 * "secondary". Both therefore take the secondary years (`Level_S1`–`Level_S4`),
 * and the form says so where the two names are read side by side.
 *
 * The three older groups reuse the record vocabulary (`Level_A*`, `Level_B*`,
 * `Level_S*`) rather than a second list of their own, so a classroom's year and a
 * capsule's year are the *same* fact and a room can pin its work to its own year.
 */
export const CLASSROOM_LEVEL_GRADES: Record<
  string,
  {label: ReturnType<typeof msg>; value: string}[]
> = {
  Kindergarten: KINDERGARTEN_GRADES,
  'Primary School': CAPSULE_GRADES.primary,
  'Middle School': CAPSULE_GRADES.college,
  'Secondary School': CAPSULE_GRADES.secondary,
  'High School': CAPSULE_GRADES.secondary,
}

export const CAPSULE_ALL_SUBJECTS = [
  {label: msg`🇸🇦 Arabic`, value: 'Arabic'},
  {label: msg`🇫🇷 French`, value: 'French'},
  {label: msg`🇬🇧 English`, value: 'English'},
  {label: msg`🇪🇸 Spanish`, value: 'SPANISH'},
  {label: msg`🇩🇪 German`, value: 'GERMAN'},
  {label: msg`🇮🇹 Italian`, value: 'ITALIAN'},
  {label: msg`🔢 Mathematics`, value: 'Maths'},
  {label: msg`📜🗺️ History & Geography`, value: 'History_Geography'},
  {label: msg`⚖️ Civics`, value: 'Civics'},
  {label: msg`🛐 Religious Studies`, value: 'Religious_Studies'},
  {label: msg`🧐 Philosophy`, value: 'Philosophy'},
  {label: msg`💡 Physics`, value: 'Physics'},
  {label: msg`🔬🧬 Earth & Life Sciences`, value: 'Earth_Life_Sciences'},
  {label: msg`🔬🧬 Science Primary`, value: 'SCIENCE_PRIMARY'},
  {label: msg`⚙️ Mechanical`, value: 'MECHANICAL'},
  {label: msg`🔌 Electricity`, value: 'ELECTRICITY'},
  {label: msg`🛠️ Technical`, value: 'TECHNICAL'},
  {label: msg`💻 Computer Science`, value: 'IT'},
  {label: msg`📊 Economy Management`, value: 'ECONOMY_MANAGEMENT'},
]

const ALL_SUBJECTS = CAPSULE_ALL_SUBJECTS

/**
 * Which family of years a grade code belongs to.
 *
 * `Level_A5` is a primary-school year, `Level_B1` a college one, `Level_S2` a
 * secondary one — a fact the grade code carries in its own prefix, which is the
 * only place it is written down. Callers that are handed a level and have to
 * reopen the picker on it (the publisher, opened from a module that already knows
 * its level and subject) need this to say which list of years to show; without it
 * they would each write the same three `startsWith` checks.
 *
 * `null` means "not one of ours", which is a real answer: a code written by an
 * older build, or by a school naming its own years, has no family to belong to.
 */
export function categoryOfLevel(
  level: string | null | undefined,
): string | null {
  if (!level) return null
  for (const category of CAPSULE_CATEGORIES) {
    if (CAPSULE_GRADES[category.value].some(g => g.value === level)) {
      return category.value
    }
  }
  return null
}

export function getAvailableSubjects(grade: string | null) {
  if (!grade) return []

  const isPrimary = grade.startsWith('Level_A')
  const isCollege = grade.startsWith('Level_B')
  const isSecondary = grade.startsWith('Level_S')

  if (isPrimary) {
    const subjects = ['Arabic', 'Maths', 'SCIENCE_PRIMARY', 'Religious_Studies']
    if (['Level_A3', 'Level_A4', 'Level_A5', 'Level_A6'].includes(grade)) {
      subjects.push('French', 'Civics')
    }
    if (['Level_A4', 'Level_A5', 'Level_A6'].includes(grade)) {
      subjects.push('English')
    }
    if (['Level_A5', 'Level_A6'].includes(grade)) {
      subjects.push('History_Geography')
    }
    return ALL_SUBJECTS.filter(s => subjects.includes(s.value))
  }

  if (isCollege) {
    const subjects = [
      'Arabic',
      'French',
      'English',
      'Maths',
      'History_Geography',
      'Civics',
      'Religious_Studies',
      'Earth_Life_Sciences',
      'IT',
      'TECHNICAL',
    ]
    if (['Level_B2', 'Level_B3'].includes(grade)) {
      subjects.push('Physics')
    }
    return ALL_SUBJECTS.filter(s => subjects.includes(s.value))
  }

  if (isSecondary) {
    const subjects = [
      'Arabic',
      'French',
      'English',
      'Maths',
      'History_Geography',
      'Civics',
      'Religious_Studies',
      'Earth_Life_Sciences',
      'Physics',
      'IT',
      'TECHNICAL',
    ]
    if (grade !== 'Level_S1') {
      subjects.push(
        'SPANISH',
        'GERMAN',
        'ITALIAN',
        'MECHANICAL',
        'ELECTRICITY',
        'ECONOMY_MANAGEMENT',
      )
    }
    if (grade === 'Level_S4') {
      subjects.push('Philosophy')
    }
    return ALL_SUBJECTS.filter(s => subjects.includes(s.value))
  }

  return ALL_SUBJECTS
}

/**
 * The grade code a published book carries, as a person would name it.
 *
 * `Level_B1` is how the record stores "7th year", and the code is what a filter
 * wants; a reader looking at a book wants the words. The *descriptor* comes back
 * rather than a string, so the caller translates it in the reader's own language
 * and this module stays out of the i18n instance. Null means "no name for this
 * code", not "no grade": the caller prints the code as it stands and still says
 * something true.
 */
export function gradeLabel(
  level: string | null | undefined,
): MacroMessageDescriptor | null {
  if (!level) return null
  for (const grades of [
    ...Object.values(CAPSULE_GRADES),
    KINDERGARTEN_GRADES,
  ]) {
    const found = grades.find(g => g.value === level)
    if (found) return found.label
  }
  return null
}

/**
 * The letters and digits of a grade, with the keycap and the spacing thrown away.
 *
 * The same year is written two ways in this app and both are "the same string" to
 * a reader: a module stores the code (`Level_A1`) and prints the label the record
 * vocabulary gives it (`1️⃣ 1st year`), while onboarding writes down what the person
 * *picked* (`1st year`, from the country's own curriculum). Comparing them
 * literally never matches — the label carries a keycap and the pick does not — so
 * the comparison is between what a person would read out loud.
 */
function gradeKey(value: string): string {
  return (
    value
      .toLowerCase()
      /*
       * The keycap goes *with* its digit, and not just around it: `1️⃣ 1st year`
       * read as bare letters and digits is `11st year`, which matches nothing.
       */
      .replace(/\d\ufe0f?\u20e3/g, '')
      .replace(/[^a-z0-9]/g, '')
  )
}

/**
 * Whether a person's own grade is the level a module is set at.
 *
 * Used to answer *is this reader in the year this course is for* when the account
 * type is missing — which, on most deployments today, it is (see
 * `#/lib/moduleAudience`). Either side saying nothing is *agreement*: an absent
 * field is not a different year, and refusing on evidence the server does not send
 * would hide the feature from every learner it is built for. A grade the vocabulary
 * has no name for is treated the same way, because a module at a level nobody can
 * name cannot be disagreed with.
 */
export function levelAgreesWithGrade(
  level: string | null | undefined,
  grade: string | null | undefined,
): boolean {
  if (!level || !grade) return true
  /* Two codes, or two labels the same: the answer, before any translation. */
  if (level === grade) return true
  const known = gradeLabel(level)?.message
  if (!known) return true
  const key = gradeKey(known)
  return key.length > 0 && key === gradeKey(grade)
}

/** The subject code a published book carries, as a person would name it. */
export function subjectLabel(
  subject: string | null | undefined,
): MacroMessageDescriptor | null {
  if (!subject) return null
  return CAPSULE_ALL_SUBJECTS.find(s => s.value === subject)?.label ?? null
}

/** The stage a grade belongs to, or null when nothing claims the code. */
export function gradeStage(level: string | null | undefined): string | null {
  if (!level) return null
  for (const category of CAPSULE_CATEGORIES) {
    const found = (CAPSULE_GRADES[category.value] ?? []).some(
      g => g.value === level,
    )
    if (found) return category.value
  }
  return null
}

/**
 * The grade code as a card names it, with no picker emoji and no stage: what
 * travels beside the stage so the two read as one answer.
 */
export function gradeShortLabel(
  level: string | null | undefined,
): MacroMessageDescriptor | null {
  if (!level) return null
  for (const grades of Object.values(CAPSULE_GRADES)) {
    const found = grades.find(g => g.value === level)
    if (found) return found.shortLabel
  }
  return null
}

/** The stage code as a card names it. */
export function categoryShortLabel(
  category: string | null | undefined,
): MacroMessageDescriptor | null {
  if (!category) return null
  return CAPSULE_CATEGORIES.find(c => c.value === category)?.shortLabel ?? null
}

/**
 * Everything a card needs to say about a grade, stage first.
 *
 * The stage is not decoration. Three of the four stages have a "2nd year", so
 * a chip that says only that has told the reader nothing they can act on; a
 * grade code nobody recognises yields no words at all rather than a raw code,
 * because `Level_X9` on a card is worse than silence.
 */
export function gradeMetaWords(
  level: string | null | undefined,
): MacroMessageDescriptor[] {
  const words: MacroMessageDescriptor[] = []
  const stage = categoryShortLabel(gradeStage(level))
  if (stage) words.push(stage)
  const name = gradeShortLabel(level)
  if (name) words.push(name)
  return words
}
